import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { client } from '../generated/client.gen';

// ---- Types (mirror JobsEndpoints.cs DTOs; ASP.NET serializes camelCase) ----

export type JobSettingType = 'string' | 'bool' | 'number' | 'enum';

export interface JobSettingField {
  key: string;
  label: string;
  type: JobSettingType;
  default: unknown;
  options?: string[] | null;
  help?: string | null;
}

export interface JobRunSummary {
  id: string;
  jobKey: string;
  status: string; // running | succeeded | failed
  triggeredBy: string; // schedule | manual
  startedAt: string;
  finishedAt?: string | null;
  durationMs?: number | null;
  totals: Record<string, number>;
  error?: string | null;
}

export interface JobRunTenant {
  tenantId: string;
  tenantName?: string | null;
  counters: Record<string, number>;
  note?: string | null;
}

export interface JobRunDetail extends JobRunSummary {
  workerId?: string | null;
  tenants: JobRunTenant[];
}

export interface JobCatalogItem {
  key: string;
  displayName: string;
  description: string;
  scope: string; // global | perTenant
  enabled: boolean;
  cron: string;
  defaultCron: string;
  queue: string;
  nextExecution?: string | null;
  settings: Record<string, unknown>;
  settingsSchema: JobSettingField[];
  lastRun?: JobRunSummary | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface UpdateJobConfigBody {
  enabled: boolean;
  cron?: string | null;
  settings?: Record<string, unknown>;
}

export interface ValidateCronResult {
  valid: boolean;
  error?: string | null;
  next: string[];
}

interface JobRunListResponse {
  data: JobRunSummary[];
  total: number;
}

// ---- Per-tenant job overrides (mirror TeamJobsEndpoints.cs DTOs) ----

export interface TeamJob {
  key: string;
  displayName: string;
  description: string;
  enabled: boolean | null; // tenant tri-state override (null = inherit global)
  enabledForTenant: boolean; // resolved: globalEnabled && (enabled ?? true)
  globalEnabled: boolean;
  settings: Record<string, unknown>;
  settingsSchema: JobSettingField[];
}

export interface UpdateTeamJobBody {
  enabled: boolean | null;
  settings?: Record<string, unknown>;
}

/**
 * One job run as seen by a tenant (mirror of TeamJobsEndpoints.TenantJobRunDto):
 * run-level status/timing plus only this tenant's counters/note slice.
 */
export interface TenantJobRun {
  id: string;
  jobKey: string;
  status: string; // running | succeeded | failed
  triggeredBy: string; // schedule | manual
  startedAt: string;
  finishedAt?: string | null;
  durationMs?: number | null;
  counters: Record<string, number>;
  note?: string | null;
  error?: string | null;
}

interface TenantJobRunListResponse {
  data: TenantJobRun[];
  total: number;
}

const jobsKey = ['admin', 'jobs'] as const;
const catalogKey = ['admin', 'jobs', 'config', 'catalog'] as const;
const configKey = (key: string) => ['admin', 'jobs', 'config', key] as const;
const runsKey = (key: string, from: number, count: number) =>
  ['admin', 'jobs', 'runs', key, from, count] as const;
const runKey = (runId: string) => ['admin', 'jobs', 'run', runId] as const;
const teamJobsKey = ['team', 'jobs'] as const;
const teamJobKey = (key: string) => ['team', 'jobs', key] as const;
const teamRunsKey = (key: string, from: number, count: number) =>
  ['team', 'jobs', 'runs', key, from, count] as const;
const teamRunKey = (runId: string) => ['team', 'jobs', 'run', runId] as const;

function useJobCatalog() {
  return useQuery({
    queryKey: catalogKey,
    queryFn: async (): Promise<JobCatalogItem[]> => {
      const { data } = await client.instance.get<{ data: JobCatalogItem[] }>('/admin/jobs/catalog');
      return data.data;
    },
  });
}

function useJobConfig(key: string) {
  return useQuery({
    queryKey: configKey(key),
    queryFn: async (): Promise<JobCatalogItem> => {
      const { data } = await client.instance.get<JobCatalogItem>(`/admin/jobs/config/${key}`);
      return data;
    },
  });
}

function useUpdateJobConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      key,
      body,
    }: {
      key: string;
      body: UpdateJobConfigBody;
    }): Promise<JobCatalogItem> => {
      const { data } = await client.instance.put<JobCatalogItem>(`/admin/jobs/config/${key}`, body);
      return data;
    },
    onSuccess: (_data, { key }) => {
      qc.invalidateQueries({ queryKey: jobsKey });
      qc.invalidateQueries({ queryKey: configKey(key) });
    },
  });
}

function useValidateCron() {
  return useMutation({
    mutationFn: async (cron: string): Promise<ValidateCronResult> => {
      const { data } = await client.instance.post<ValidateCronResult>('/admin/jobs/validate-cron', {
        cron,
      });
      return data;
    },
  });
}

function useJobRuns(key: string, { from, count }: { from: number; count: number }) {
  return useQuery({
    queryKey: runsKey(key, from, count),
    queryFn: async (): Promise<JobRunListResponse> => {
      const { data } = await client.instance.get<JobRunListResponse>(`/admin/jobs/${key}/runs`, {
        params: { from, count },
      });
      return data;
    },
  });
}

function useJobRun(runId: string | null) {
  return useQuery({
    queryKey: runKey(runId ?? ''),
    enabled: !!runId,
    queryFn: async (): Promise<JobRunDetail> => {
      const { data } = await client.instance.get<JobRunDetail>(`/admin/jobs/runs/${runId}`);
      return data;
    },
  });
}

function useTenantJobs() {
  return useQuery({
    queryKey: teamJobsKey,
    queryFn: async (): Promise<TeamJob[]> => {
      const { data } = await client.instance.get<{ data: TeamJob[] }>('/team/jobs');
      return data.data;
    },
  });
}

function useTenantJob(key: string) {
  return useQuery({
    queryKey: teamJobKey(key),
    queryFn: async (): Promise<TeamJob> => {
      const { data } = await client.instance.get<TeamJob>(`/team/jobs/${key}`);
      return data;
    },
  });
}

function useTenantJobRuns(key: string, { from, count }: { from: number; count: number }) {
  return useQuery({
    queryKey: teamRunsKey(key, from, count),
    queryFn: async (): Promise<TenantJobRunListResponse> => {
      const { data } = await client.instance.get<TenantJobRunListResponse>(
        `/team/jobs/${key}/runs`,
        { params: { from, count } },
      );
      return data;
    },
  });
}

function useTenantJobRun(runId: string | null) {
  return useQuery({
    queryKey: teamRunKey(runId ?? ''),
    enabled: !!runId,
    queryFn: async (): Promise<TenantJobRun> => {
      const { data } = await client.instance.get<TenantJobRun>(`/team/jobs/runs/${runId}`);
      return data;
    },
  });
}

function useUpdateTenantJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, body }: { key: string; body: UpdateTeamJobBody }): Promise<TeamJob> => {
      const { data } = await client.instance.put<TeamJob>(`/team/jobs/${key}`, body);
      return data;
    },
    onSuccess: () => {
      // teamJobsKey is a prefix of teamJobKey(key) — invalidating it covers both
      // the list and the single-job read used by the detail page.
      qc.invalidateQueries({ queryKey: teamJobsKey });
    },
  });
}

export const jobsConfigApi = {
  useJobCatalog,
  useJobConfig,
  useUpdateJobConfig,
  useValidateCron,
  useJobRuns,
  useJobRun,
  useTenantJobs,
  useTenantJob,
  useTenantJobRuns,
  useTenantJobRun,
  useUpdateTenantJob,
};
